// client/src/components/auth/ForceChangePasswordPage.js - VERSÃO CORRIGIDA
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/auth/AuthProvider';
import useAlert from '../../hooks/useAlert';
import { useAuthMutations } from '../../hooks/useAuth';
import { Loader2, Save, KeyRound, LogOut } from 'lucide-react';

const ForceChangePasswordPage = () => {
    const navigate = useNavigate();
    const { setAlert } = useAlert();
    const { user, isAuthenticated, isLoading: authLoading } = useAuth();
    const { forceUpdatePassword, logout } = useAuthMutations();

    const [formData, setFormData] = useState({ 
        newPassword: '', 
        confirmNewPassword: '' 
    });
    const [isUpdating, setIsUpdating] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const { newPassword, confirmNewPassword } = formData;

    // ✅ CORREÇÃO: Verificação mais robusta
    useEffect(() => {
        console.log('🔍 [ForceChangePassword] Estado:', {
            authLoading,
            isAuthenticated,
            user: user ? {
                id: user._id,
                email: user.email,
                requirePasswordChange: user.requirePasswordChange,
                hasAccount: !!user.account,
                accountsCount: user.accounts?.length,
                franquiasCount: user.franquias?.length
            } : null
        });

        if (!authLoading) {
            if (!isAuthenticated) {
                console.log('⚠️ [ForceChangePassword] Não autenticado, redirecionando para login');
                navigate('/login', { replace: true });
                return;
            }
            
            if (user) {
                // ✅ CORREÇÃO: Verificar de várias formas se precisa trocar senha
                const needsPasswordChange = 
                    user.requirePasswordChange === true ||
                    user.requirePasswordChange === 'true' ||
                    user.forcePasswordChange === true ||
                    (user.metadata && user.metadata.requirePasswordChange === true);
                
                console.log('🔍 [ForceChangePassword] Precisa trocar senha?', {
                    requirePasswordChange: user.requirePasswordChange,
                    forcePasswordChange: user.forcePasswordChange,
                    metadata: user.metadata,
                    needsPasswordChange
                });
                
                if (!needsPasswordChange) {
                    console.log('ℹ️ [ForceChangePassword] Usuário não precisa trocar senha, redirecionando');
                    navigate('/', { replace: true });
                }
            }
        }
    }, [user, isAuthenticated, authLoading, navigate]);

    const onChange = e => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        
        // Limpar erro de validação quando o usuário começa a digitar
        if (validationErrors[name]) {
            setValidationErrors({
                ...validationErrors,
                [name]: null
            });
        }
    };

    const validateForm = () => {
        const errors = {};
        
        if (!newPassword) {
            errors.newPassword = 'A nova senha é obrigatória';
        } else if (newPassword.length < 6) {
            errors.newPassword = 'A senha deve ter pelo menos 6 caracteres';
        }
        
        if (!confirmNewPassword) {
            errors.confirmNewPassword = 'Confirmação de senha é obrigatória';
        } else if (newPassword !== confirmNewPassword) {
            errors.confirmNewPassword = 'As senhas não coincidem';
        }
        
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const onSubmit = async e => {
        e.preventDefault();
        
        console.log('🔍 [ForceChangePassword] onSubmit chamado:', { 
            newPassword: newPassword ? '***' : 'vazio', 
            confirmNewPassword: confirmNewPassword ? '***' : 'vazio' 
        });
        
        if (!validateForm()) {
            return;
        }

        setIsUpdating(true);
        try {
            console.log('🔄 [ForceChangePassword] Chamando forceUpdatePassword...');
            
            // ✅ CORREÇÃO: Usar mutation corretamente
            await forceUpdatePassword.mutateAsync({ newPassword });
            
            setAlert('Senha definida com sucesso! Redirecionando...', 'success', 3000);
            
        } catch (err) {
            console.error('❌ [ForceChangePassword] Erro:', err);
            console.error('❌ [ForceChangePassword] Response:', err.response);
            
            const errorMessage = err.response?.data?.error || 
                               err.response?.data?.message || 
                               'Erro ao definir a nova senha';
            
            setAlert(errorMessage, 'danger');
        } finally {
            setIsUpdating(false);
        }
    };
    
    const handleCancelAndLogout = async () => {
        try {
            await logout.mutateAsync();
        } catch (err) {
            console.error('❌ Erro ao fazer logout:', err);
            navigate('/login');
        }
    };

    const isLoading = authLoading || isUpdating || forceUpdatePassword.isPending;

    // ✅ CORREÇÃO: Adicionar classes CSS se não existirem
    const containerStyle = {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8fafc',
        padding: '1rem'
    };

    const cardStyle = {
        background: 'white',
        borderRadius: '0.5rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        padding: '2rem',
        width: '100%',
        maxWidth: '28rem'
    };

    return (
        <div style={containerStyle}>
            <div style={cardStyle}>
                <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                    <KeyRound size={48} style={{ color: '#3b82f6', margin: '0 auto 1rem' }} />
                    <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                        Definir Nova <span style={{ color: '#3b82f6' }}>Senha</span>
                    </h1>
                    <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
                        Por segurança, você precisa definir uma nova senha para sua conta antes de continuar.
                    </p>
                </div>
                
                <form onSubmit={onSubmit} noValidate>
                    <div style={{ marginBottom: '1rem' }}>
                        <label 
                            htmlFor="newPassword" 
                            style={{ 
                                display: 'block', 
                                fontSize: '0.875rem', 
                                fontWeight: '500', 
                                color: '#374151',
                                marginBottom: '0.25rem'
                            }}
                        >
                            Nova Senha* (mínimo 6 caracteres)
                        </label>
                        <input 
                            type="password" 
                            name="newPassword" 
                            id="newPassword" 
                            value={newPassword} 
                            onChange={onChange} 
                            required 
                            minLength="6" 
                            style={{
                                width: '100%',
                                padding: '0.5rem 0.75rem',
                                border: `1px solid ${validationErrors.newPassword ? '#ef4444' : '#d1d5db'}`,
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem'
                            }}
                            autoComplete="new-password" // ✅ CORREÇÃO: Adicionar autocomplete
                            disabled={isLoading}
                        />
                        {validationErrors.newPassword && (
                            <p style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                                {validationErrors.newPassword}
                            </p>
                        )}
                    </div>
                    
                    <div style={{ marginBottom: '1.5rem' }}>
                        <label 
                            htmlFor="confirmNewPassword" 
                            style={{ 
                                display: 'block', 
                                fontSize: '0.875rem', 
                                fontWeight: '500', 
                                color: '#374151',
                                marginBottom: '0.25rem'
                            }}
                        >
                            Confirmar Nova Senha*
                        </label>
                        <input 
                            type="password" 
                            name="confirmNewPassword" 
                            id="confirmNewPassword" 
                            value={confirmNewPassword} 
                            onChange={onChange} 
                            required 
                            minLength="6" 
                            style={{
                                width: '100%',
                                padding: '0.5rem 0.75rem',
                                border: `1px solid ${validationErrors.confirmNewPassword ? '#ef4444' : '#d1d5db'}`,
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem'
                            }}
                            autoComplete="new-password" // ✅ CORREÇÃO: Adicionar autocomplete
                            disabled={isLoading}
                        />
                        {validationErrors.confirmNewPassword && (
                            <p style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                                {validationErrors.confirmNewPassword}
                            </p>
                        )}
                    </div>
                    
                    <div style={{ marginBottom: '1.5rem' }}>
                        <button 
                            type="submit" 
                            style={{
                                width: '100%',
                                backgroundColor: '#3b82f6',
                                color: 'white',
                                padding: '0.5rem 1rem',
                                borderRadius: '0.375rem',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                opacity: isLoading ? 0.7 : 1
                            }}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <Loader2 style={{ animation: 'spin 1s linear infinite' }} size={20} />
                            ) : (
                                <Save size={20} />
                            )}
                            <span>Salvar Nova Senha</span>
                        </button>
                    </div>
                </form>
                
                <div style={{ textAlign: 'center' }}>
                    <button 
                        onClick={handleCancelAndLogout}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#6b7280',
                            fontSize: '0.875rem',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                        }}
                        disabled={isLoading}
                    >
                        <LogOut size={16} />
                        <span>Cancelar e Sair</span>
                    </button>
                </div>
                
                {/* ✅ Adicionar mensagem de debug se necessário */}
                {process.env.NODE_ENV === 'development' && user && (
                    <div style={{ 
                        marginTop: '1rem', 
                        padding: '0.75rem', 
                        backgroundColor: '#f3f4f6', 
                        borderRadius: '0.375rem',
                        fontSize: '0.75rem',
                        color: '#6b7280'
                    }}>
                        <strong>Debug Info:</strong><br />
                        User ID: {user._id}<br />
                        Require Password Change: {String(user.requirePasswordChange)}<br />
                        Has Account: {String(!!user.account)}<br />
                        Accounts: {user.accounts?.length || 0}<br />
                        Franquias: {user.franquias?.length || 0}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ForceChangePasswordPage;